import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { CategoryWithChildren } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

/* ── Open-Meteo (free, no API key) ── */
const GRADAC_LAT = 43.366;
const GRADAC_LON = 20.532;
const WEATHER_URL =
  `https://api.open-meteo.com/v1/forecast?latitude=${GRADAC_LAT}&longitude=${GRADAC_LON}` +
  `&current=temperature_2m,weathercode,windspeed_10m,relative_humidity_2m` +
  `&daily=weathercode,temperature_2m_max,temperature_2m_min` +
  `&timezone=Europe/Belgrade&forecast_days=7`;

interface WeatherCurrent {
  temperature: number;
  weathercode: number;
  windspeed: number;
  humidity: number;
}
interface WeatherDay {
  date: string;
  weathercode: number;
  tempMax: number;
  tempMin: number;
}

/* WMO Weather interpretation codes → emoji + label */
const weatherLabel = (code: number): { icon: string; label: string } => {
  if (code === 0) return { icon: '☀️', label: 'Ведро' };
  if (code <= 3) return { icon: '⛅', label: 'Делимично облачно' };
  if (code <= 48) return { icon: '🌫️', label: 'Магла' };
  if (code <= 55) return { icon: '🌦️', label: 'Ситна киша' };
  if (code <= 57) return { icon: '🌧️', label: 'Ледена киша' };
  if (code <= 65) return { icon: '🌧️', label: 'Киша' };
  if (code <= 67) return { icon: '🌧️', label: 'Ледена киша' };
  if (code <= 75) return { icon: '🌨️', label: 'Снег' };
  if (code <= 77) return { icon: '🌨️', label: 'Снежна зрна' };
  if (code <= 82) return { icon: '🌧️', label: 'Пљусак' };
  if (code <= 86) return { icon: '🌨️', label: 'Снежни пљусак' };
  if (code <= 99) return { icon: '⛈️', label: 'Грмљавина' };
  return { icon: '🌤️', label: 'Непознато' };
};

const dayName = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['Нед', 'Пон', 'Уто', 'Сре', 'Чет', 'Пет', 'Суб'];
  return days[d.getDay()];
};

/* ── Render description with **bold** and \n ── */
const renderDescription = (text: string, t: (s: string) => string) => {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <React.Fragment key={i}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{t(part)}</strong> : <span key={j}>{t(part)}</span>
        )}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const sectionIcons: Record<string, string> = {
  'kako-doci': '🗺️',
  'restorani-hoteli': '🏨',
  'vremenska-prognoza': '⛅',
  'pravila-posete': '📜',
};

const VisitPage: React.FC = () => {
  const { t } = useScript();
  const langFade = useLangFade();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState<CategoryWithChildren | null>(null);
  useDocumentTitle('Посета — Манастир Градац', '/poseta', category?.description);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [weather, setWeather] = useState<WeatherCurrent | null>(null);
  const [forecast, setForecast] = useState<WeatherDay[]>([]);
  const [weatherError, setWeatherError] = useState(false);

  // Load the "poseta" category with its children
  const loadCategory = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getCategoryBySlug('poseta')
      .then(data => { setCategory(data); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || 'Грешка при учитавању.');
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadCategory(); }, [loadCategory]);

  // Load weather
  useEffect(() => {
    fetch(WEATHER_URL)
      .then(r => r.json())
      .then(data => {
        if (data.current) {
          setWeather({
            temperature: data.current.temperature_2m,
            weathercode: data.current.weathercode,
            windspeed: data.current.windspeed_10m,
            humidity: data.current.relative_humidity_2m,
          });
        }
        if (data.daily) {
          const days: WeatherDay[] = data.daily.time.map((d: string, i: number) => ({
            date: d,
            weathercode: data.daily.weathercode[i],
            tempMax: data.daily.temperature_2m_max[i],
            tempMin: data.daily.temperature_2m_min[i],
          }));
          setForecast(days);
        }
      })
      .catch(() => setWeatherError(true));
  }, []);

  if (loading) {
    return (
      <div className="visit-page">
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visit-page">
        <div className="state-block state-error" role="alert">
          <span>{t(error)}</span>
        </div>
      </div>
    );
  }

  // Split children into sections
  const children = category?.children || [];

  return (
    <div className="visit-page">
      {/* Hero header */}
      <div className="visit-hero">
        <div className="visit-hero-overlay" />
        <div className="visit-hero-content">
          <h1 className="news-heading">{t('Посета Манастиру')}</h1>
        </div>
      </div>

      <div className={`visit-body ${langFade}`}>
        {/* ─── Google Maps ─── */}
        <section className="visit-section visit-map-section">
          <div className="visit-section-header">
            <span className="visit-section-icon">📍</span>
            <h2 className="visit-section-title">{t('Локација')}</h2>
          </div>
          <div className="visit-map-frame">
            <iframe
              title="Карта локације манастира Градац"
              aria-label="Карта локације манастира Градац"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8203.916466589275!2d20.53193794764461!3d43.366098133951674!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475634f5e9285a3b%3A0x890792859db56b85!2sMonastery%20Gradac!5e0!3m2!1sen!2srs!4v1765818881060!5m2!1sen!2srs"
              className="visit-google-map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>

        {/* ─── Weather Section ─── */}
        <section className="visit-section visit-weather-section">
          <div className="visit-section-header">
            <span className="visit-section-icon">⛅</span>
            <h2 className="visit-section-title">{t('Временска прогноза')}</h2>
          </div>

          {weatherError ? (
            <p className="visit-weather-error">{t('Тренутно није могуће учитати временску прогнозу.')}</p>
          ) : weather ? (
            <div className="visit-weather-content">
              {/* Current weather */}
              <div className="visit-weather-current">
                <div className="visit-weather-current-icon">
                  {weatherLabel(weather.weathercode).icon}
                </div>
                <div className="visit-weather-current-info">
                  <span className="visit-weather-temp">{Math.round(weather.temperature)}°C</span>
                  <span className="visit-weather-desc">{t(weatherLabel(weather.weathercode).label)}</span>
                  <div className="visit-weather-details">
                    <span>💨 {Math.round(weather.windspeed)} km/h</span>
                    <span>💧 {weather.humidity}%</span>
                  </div>
                </div>
                <div className="visit-weather-location">
                  <span className="visit-weather-place">{t('Градац, Србија')}</span>
                  <span className="visit-weather-now">{t('Тренутно')}</span>
                </div>
              </div>

              {/* 7-day forecast */}
              <div className="visit-forecast">
                {forecast.map((day, i) => {
                  const wl = weatherLabel(day.weathercode);
                  return (
                    <div key={day.date} className={`visit-forecast-day ${i === 0 ? 'today' : ''}`}>
                      <span className="visit-forecast-name">
                        {i === 0 ? t('Данас') : t(dayName(day.date))}
                      </span>
                      <span className="visit-forecast-icon">{wl.icon}</span>
                      <span className="visit-forecast-temps">
                        <span className="temp-max">{Math.round(day.tempMax)}°</span>
                        <span className="temp-min">{Math.round(day.tempMin)}°</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="state-block" role="status">
              <div className="state-spinner" aria-hidden="true" />
            </div>
          )}
        </section>

        {/* ─── Dynamic sections from category children ─── */}
        {children
          .filter(c => c.slug !== 'vremenska-prognoza') // weather is handled above
          .map(child => (
            <section key={child.id} className="visit-section visit-info-section">
              <div className="visit-section-header">
                <span className="visit-section-icon">
                  {sectionIcons[child.slug] || '📍'}
                </span>
                <h2 className="visit-section-title">{t(child.title)}</h2>
                {isAdmin && (
                  <button
                    className="visit-admin-edit"
                    onClick={() => navigate(`/${child.slug}/uredi`)}
                  >
                    {t('Уреди')} ✎
                  </button>
                )}
              </div>

              {child.coverImageUrl && (
                <div className="visit-section-cover">
                  <img
                    src={child.coverImageUrl}
                    alt={child.title}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}

              <div className="visit-section-text">
                <p>{renderDescription(child.description, t)}</p>
              </div>
            </section>
          ))}

        {/* Admin: edit the parent "poseta" category */}
        {isAdmin && category && (
          <div className="visit-admin-toolbar">
            <button
              className="btn-primary"
              onClick={() => navigate('/poseta/uredi')}
            >
              {t('Уреди главну категорију "Посета"')} ✎
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitPage;
